// Swipe Service
// Service layer for swipe and matching functionality

import { SwipeAPI, SwipeCard, UserProfile, CardTeaser, CardRevealed, KeepPileEntry, KeepPileAllowance, SwipeAllowance, PersonalitySuit, UserPreferences, MatchResult, SwipeAction, MatchRequest, BetLevel } from '../../types/swipe';
import { mockSwipeAPI, defaultUserPreferences } from './mockData';
import { logger } from '../../utils/logger';
import { config } from '../../config/env';
import { authInterceptor } from '../Authentication/authInterceptor';

const MATCHING_BASE = `${config.api.baseUrl}/matching`;
const USERS_BASE = `${config.api.baseUrl}/users`;

// Maps a raw bet_requests DB row (snake_case) onto the frontend MatchRequest
// shape. Status values are intentionally identical on both sides, so no
// case conversion is needed there.
function toMatchRequest(row: any): MatchRequest {
  return {
    id: row.id,
    senderId: row.sender_id,
    receiverId: row.receiver_id,
    betLevel: row.bet_level,
    message: row.message ?? null,
    initialMessage: row.message ?? null,
    status: row.status,
    response: {
      // Map the negotiation status onto the poker-themed response action.
      action: row.status === 'DECLINED' ? 'FOLD' : row.status === 'COUNTER' ? 'RAISE' : row.status === 'ACCEPTED' ? 'CALL' : null,
      counterBet: row.counter_bet_level ?? null,
      counterMessage: row.counter_message ?? null,
      respondedAt: row.responded_at ?? null,
    },
    // Needed to open the conversation the moment a bet is called — it was
    // dropped here, so the accept response knew where the chat was and the UI
    // did not.
    conversationId: row.conversation_id ?? null,
    sentAt: row.created_at,
    expiresAt: row.expires_at,
    remindersSent: [],
    finalBetLevel: row.status === 'ACCEPTED' ? (row.counter_bet_level ?? row.bet_level) : null,
    matchedAt: row.status === 'ACCEPTED' ? (row.responded_at ?? null) : null,
    // Names enriched by the backend's listBetRequests; surfaced loosely so
    // callers that only need a label don't have to load a full profile.
    senderProfile: row.sender_first_name ? ({ name: row.sender_first_name } as any) : undefined,
    receiverProfile: row.receiver_first_name ? ({ name: row.receiver_first_name } as any) : undefined,
  };
}

// Maps a raw card from the backend /matching/cards endpoint (a flat object
// from matching.service.assembleCard) onto the nested SwipeCard shape the UI
// renders (user / teaser / revealed). Without this, real cards silently fail
// and the deck falls back to mock data.
function toSwipeCard(row: any): SwipeCard {
  const rawPhotos: any[] = Array.isArray(row.photos) ? row.photos : [];

  const imageRows = rawPhotos.filter((p) => p && p.photo_url && p.media_type !== 'video');
  // Main image first, everything else in its stored order. Every consumer that
  // wants one picture takes photos[0], so this is what makes an avatar the
  // face its owner chose rather than whichever upload happened to sort first.
  const imageUrls: string[] = [
    ...imageRows.filter((p) => p.is_main),
    ...imageRows.filter((p) => !p.is_main),
  ].map((p) => p.photo_url as string);

  // The MAIN video, not merely the first one on file. Photos come back ordered
  // by display_order, so `find` was picking whichever video happened to sit
  // earliest — often not the one the person chose to lead with.
  const videoPhoto =
    rawPhotos.find((p) => p && p.media_type === 'video' && p.photo_url && p.is_main) ||
    rawPhotos.find((p) => p && p.media_type === 'video' && p.photo_url);

  // An IMAGE, always. This used to take the first row flagged is_main whatever
  // its type, which was harmless only while an account could have exactly one
  // main. Now that a main image and a main video coexist, that same line would
  // hand a video file to every avatar on the profile.
  const mainPhoto: string =
    imageUrls[0] ||
    videoPhoto?.thumbnail_url ||
    '';
  const photos: string[] = imageUrls.length > 0 ? imageUrls : (mainPhoto ? [mainPhoto] : []);

  // The poster a video card shows before its first frame arrives.
  //
  // This was the profile's main PHOTO, which is a different image from the
  // video's own opening frame — so every card flashed one picture and then
  // cut to unrelated footage. Using the video's own thumbnail makes the
  // poster and the first frame the same image, and the cut disappears.
  const videoPoster: string = videoPhoto?.thumbnail_url || mainPhoto;

  const name: string = row.name || 'Someone';
  const city: string = row.location?.city || '';
  const compatibility: number = typeof row.compatibility === 'number' ? row.compatibility : 0;
  // The card id MUST equal the target user's id — recordSwipe sends it as
  // targetUserId. assembleCard sets id === userId, so preserve that.
  const targetId: string = row.userId || row.id;

  const user: UserProfile = {
    id: targetId,
    name,
    firstName: name.split(' ')[0],
    age: row.age ?? 0,
    bio: row.bio ?? '',
    photos,
    location: city,
    interests: Array.isArray(row.interests) ? row.interests : [],
    personality: row.primaryPersona ? [row.primaryPersona] : [],
    occupation: row.occupation ?? undefined,
    education: row.education ?? undefined,
    verified: !!row.verified,
    lastActive: row.lastActive ? new Date(row.lastActive) : undefined,
  };

  const teaser: CardTeaser = {
    photo: mainPhoto,
    video: videoPhoto?.photo_url,
    videoPoster,
    name,
    age: user.age,
    distance: 0,
    matchPercentage: compatibility,
    verified: !!row.verified,
    location: city,
  };

  const revealed: CardRevealed = {
    photos,
    bio: user.bio,
    interests: user.interests,
    personality: user.personality,
    occupation: user.occupation,
    education: user.education,
    compatibility,
  };

  return { id: targetId, user, teaser, revealed, isFlipped: false, isAnimating: false };
}

const KEEP_PILE_SUITS: PersonalitySuit[] = ['spades', 'hearts', 'diamonds', 'clubs'];

// Maps a backend keep_pile entry (with its enriched `card`) onto the nested
// KeepPileEntry the review UI renders. Card suit is derived from the name
// (same deterministic hash the deck uses), so it stays stable across reloads.
function toKeepPileEntry(row: any): KeepPileEntry {
  const card = toSwipeCard(row.card ?? {});
  const hash = card.user.name.split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  return {
    id: row.id,
    profileId: row.profileId ?? card.user.id,
    profile: {
      ...card.user,
      firstName: card.user.firstName || card.user.name.split(' ')[0],
      personalityType: card.user.personality?.[0] || 'Unknown',
      compatibilityScore: card.teaser.matchPercentage ?? card.revealed.compatibility ?? 0,
    },
    cardSuit: KEEP_PILE_SUITS[hash % KEEP_PILE_SUITS.length],
    addedAt: row.addedAt ?? new Date().toISOString(),
    expiresAt: row.expiresAt ?? new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    priority: row.priority,
  };
}

class SwipeService implements SwipeAPI {
  private baseUrl: string;

  constructor(baseUrl: string = config.api.baseUrl) {
    this.baseUrl = baseUrl;
  }

  private getAuthHeader(): string {
    return `Bearer ${authInterceptor.getAccessToken() || ''}`;
  }

  // Unwraps this backend's { success, data, message } response envelope
  private async unwrap(response: Response): Promise<any> {
    if (!response.ok) {
      // Carry the backend's machine code and the status on the error. Without
      // them every failure looks identical to the caller, and a daily-limit
      // 403 is indistinguishable from the network being down — which is how it
      // ends up silently falling back to mock data.
      let code = '';
      let message = response.statusText;
      try {
        const body = await response.json();
        code = body?.error || '';
        message = body?.message || message;
      } catch {
        /* non-JSON error body */
      }
      const err = new Error(message || `Request failed: ${response.status}`) as Error & {
        code?: string;
        status?: number;
      };
      err.code = code;
      err.status = response.status;
      throw err;
    }
    const body = await response.json();
    return body.data;
  }

  // Get cards for swiping (SwipeAPI interface method)
  async getCards(userId: string, limit: number = 10): Promise<SwipeCard[]> {
    try {
      if (this.baseUrl) {
        const response = await fetch(`${MATCHING_BASE}/cards?limit=${limit}`, {
          method: 'GET',
          headers: { 'Authorization': this.getAuthHeader() }
        });
        const data = await this.unwrap(response);
        return (data ?? []).map(toSwipeCard);
      }
    } catch (error) {
      console.error('Error fetching cards, falling back to mock data:', error);
    }

    // Fallback to mock data (no baseUrl configured, or the real call failed)
    return await mockSwipeAPI.getCardDeck(userId, defaultUserPreferences);
  }

  // Get card deck for swiping (extended method with preferences)
  // Note: server-side preference filtering isn't built yet — this calls the
  // same /matching/cards endpoint as getCards(); the preferences param is
  // applied client-side by callers for now.
  async getCardDeck(userId: string, preferences: UserPreferences): Promise<SwipeCard[]> {
    try {
      if (this.baseUrl) {
        const response = await fetch(`${MATCHING_BASE}/cards?limit=10`, {
          method: 'GET',
          headers: { 'Authorization': this.getAuthHeader() }
        });
        const data = await this.unwrap(response);
        return (data ?? []).map(toSwipeCard);
      }
    } catch (error) {
      console.error('Error fetching card deck, falling back to mock data:', error);
    }

    return await mockSwipeAPI.getCardDeck(userId, preferences);
  }

  // Record a swipe action
  async recordSwipe(userId: string, cardId: string, action: SwipeAction): Promise<void> {
    try {
      if (this.baseUrl) {
        const response = await fetch(`${MATCHING_BASE}/swipe`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': this.getAuthHeader() },
          body: JSON.stringify({ targetUserId: cardId, action })
        });
        await this.unwrap(response);
        return;
      }
    } catch (error: any) {
      // A reached daily limit is a real answer, not a transport failure.
      // Falling through to the mock here would record the swipe locally and
      // tell the user it worked, which is exactly how a paywall becomes
      // invisible — and unenforceable.
      if (error?.code === 'SWIPE_LIMIT_REACHED' || error?.status === 403) throw error;
      console.error('Error recording swipe, falling back to mock data:', error);
    }

    return await mockSwipeAPI.recordSwipe(userId, cardId, action);
  }

  // Get match results
  async getMatchResults(userId: string): Promise<MatchResult[]> {
    try {
      if (this.baseUrl) {
        const response = await fetch(`${MATCHING_BASE}/matches`, {
          method: 'GET',
          headers: { 'Authorization': this.getAuthHeader() }
        });
        return await this.unwrap(response);
      }
    } catch (error) {
      console.error('Error fetching matches, falling back to mock data:', error);
    }

    return await mockSwipeAPI.getMatchResults(userId);
  }

  // Update user preferences — lives on the /users domain (Phase 1), not matching
  async updatePreferences(userId: string, preferences: UserPreferences): Promise<void> {
    try {
      if (this.baseUrl) {
        const response = await fetch(`${USERS_BASE}/preferences`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'Authorization': this.getAuthHeader() },
          body: JSON.stringify(preferences)
        });
        await this.unwrap(response);
        return;
      }
    } catch (error) {
      console.error('Error updating preferences, falling back to mock data:', error);
    }

    return await mockSwipeAPI.updatePreferences(userId, preferences);
  }

  // Undo last swipe
  async undoLastSwipe(userId: string): Promise<boolean> {
    try {
      if (this.baseUrl) {
        const response = await fetch(`${MATCHING_BASE}/undo`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': this.getAuthHeader() },
        });
        const data = await this.unwrap(response);
        return data.success;
      }
    } catch (error) {
      console.error('Error undoing swipe, falling back to mock data:', error);
    }

    return await mockSwipeAPI.undoLastSwipe(userId);
  }

  // Get user's swipe statistics
  async getSwipeStats(userId: string): Promise<any> {
    try {
      if (this.baseUrl) {
        const response = await fetch(`${MATCHING_BASE}/stats`, {
          method: 'GET',
          headers: { 'Authorization': this.getAuthHeader() }
        });
        return await this.unwrap(response);
      }
    } catch (error) {
      console.error('Error fetching swipe stats, falling back to mock data:', error);
    }

    // Fallback mock stats
    return {
      totalSwipes: 150,
      likesGiven: 45,
      passesGiven: 105,
      superLikesGiven: 3,
      savesGiven: 12,
      matches: 8,
      averageSessionDuration: 15.5,
      lastActiveDate: new Date()
    };
  }

  // Get daily swipe limit status
  /**
   * Today's swipe allowance for this account, resolved from its tier.
   *
   * Returns null when it cannot be read. It used to fall back to invented
   * numbers — a 50 limit with 23 used — which the deck then displayed as
   * fact. A count that is wrong is worse than no count: it tells someone they
   * have swipes left when the server will refuse the next one.
   */
  async getDailyLimitStatus(_userId?: string): Promise<SwipeAllowance | null> {
    if (!this.baseUrl) return null;
    try {
      const response = await fetch(`${MATCHING_BASE}/limit`, {
        method: 'GET',
        headers: { 'Authorization': this.getAuthHeader() }
      });
      const data = await this.unwrap(response);
      // The endpoint calls it swipesUsed; SwipeAllowance calls it `used`, so
      // the count arrived undefined and the header's counter did arithmetic on
      // it. Mapped here rather than renaming either side, since both names
      // read correctly where they live.
      return data ? { ...data, used: data.swipesUsed ?? data.used ?? 0 } : null;
    } catch (error) {
      console.error('Error fetching daily limit status:', error);
      return null;
    }
  }

  // Check for new matches
  async checkNewMatches(userId: string): Promise<MatchResult[]> {
    try {
      if (this.baseUrl) {
        const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
        const response = await fetch(`${MATCHING_BASE}/matches?since=${encodeURIComponent(since)}`, {
          method: 'GET',
          headers: { 'Authorization': this.getAuthHeader() }
        });
        return await this.unwrap(response);
      }
    } catch (error) {
      console.error('Error checking new matches, falling back to mock data:', error);
    }

    return await mockSwipeAPI.getMatchResults(userId);
  }

  // Report inappropriate content — Safety domain isn't built yet (Phase 4)
  async reportContent(userId: string, cardId: string, reason: string): Promise<void> {
    logger.log(`Reported content: ${reason} for card ${cardId} by user ${userId} (Safety domain not yet built)`);
  }

  // Block user — Safety domain isn't built yet (Phase 4)
  async blockUser(userId: string, blockedUserId: string): Promise<void> {
    logger.log(`Blocked user ${blockedUserId} by user ${userId} (Safety domain not yet built)`);
  }

  // Check if there's a match between two users (SwipeAPI interface method)
  async checkMatch(userId: string, targetUserId: string): Promise<MatchResult> {
    try {
      if (this.baseUrl) {
        const response = await fetch(`${MATCHING_BASE}/check`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': this.getAuthHeader() },
          body: JSON.stringify({ targetUserId })
        });
        const data = await this.unwrap(response);
        return {
          isMatch: data.isMatch,
          matchId: data.matchId ?? undefined,
          message: data.isMatch ? "It's a match!" : 'No match yet',
        };
      }
    } catch (error) {
      console.error('Error checking match, falling back to mock data:', error);
    }

    // Mock implementation
    const isMatch = Math.random() > 0.7; // 30% chance of match
    return {
      isMatch,
      matchId: isMatch ? `match_${Date.now()}` : undefined,
      message: isMatch ? "It's a match!" : "No match yet"
    };
  }

  // Get user preferences — lives on the /users domain (Phase 1), not matching
  async getUserPreferences(userId: string): Promise<UserPreferences> {
    try {
      if (this.baseUrl) {
        const response = await fetch(`${USERS_BASE}/preferences`, {
          method: 'GET',
          headers: { 'Authorization': this.getAuthHeader() }
        });
        return await this.unwrap(response);
      }
    } catch (error) {
      console.error('Error getting user preferences, falling back to mock data:', error);
    }

    // Fallback to default preferences
    return defaultUserPreferences;
  }

  // Update user preferences (SwipeAPI interface method)
  async updateUserPreferences(userId: string, preferences: Partial<UserPreferences>): Promise<void> {
    try {
      if (this.baseUrl) {
        const response = await fetch(`${USERS_BASE}/preferences`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'Authorization': this.getAuthHeader() },
          body: JSON.stringify(preferences)
        });
        await this.unwrap(response);
        return;
      }
    } catch (error) {
      console.error('Error updating user preferences, falling back to mock data:', error);
    }

    // Mock implementation
    logger.log(`Updated preferences for user ${userId}:`, preferences);
  }

  // "Add to Deck": queue a profile into the user's swipe deck (front of the
  // line, re-surfaced even if previously passed). This is NOT the keep pile —
  // keeping happens by swiping Keep on the card in the deck.
  // Deck membership is server state, so when an API base is configured the API
  // is the only source of truth — a failed write throws rather than quietly
  // writing to localStorage. Swallowing the error here is what made "Add to
  // Deck" look like it worked and then forget: the button flipped to "Added",
  // nothing reached swipe_deck_queue, and the next load read false back from
  // the server. localStorage remains the mock path for local dev with no API.
  async addToDeck(userId: string, targetUserId: string): Promise<void> {
    if (this.baseUrl) {
      const response = await fetch(`${MATCHING_BASE}/deck`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': this.getAuthHeader() },
        body: JSON.stringify({ profileId: targetUserId })
      });
      await this.unwrap(response);
      return;
    }

    const deckKey = `swipe_deck_${userId}`;
    const existingDeck = JSON.parse(localStorage.getItem(deckKey) || '[]');
    if (!existingDeck.includes(targetUserId)) {
      existingDeck.push(targetUserId);
      localStorage.setItem(deckKey, JSON.stringify(existingDeck));
    }
    logger.log(`Added user ${targetUserId} to deck for user ${userId}`);
  }

  // Keep pile: save a profile you kept in the deck (so you can play a Message
  // Bet on them later). Populated by swiping Keep, not by "Add to Deck".
  async addToKeepPile(userId: string, targetUserId: string): Promise<void> {
    try {
      if (this.baseUrl) {
        const response = await fetch(`${MATCHING_BASE}/keep`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': this.getAuthHeader() },
          body: JSON.stringify({ profileId: targetUserId })
        });
        await this.unwrap(response);
        return;
      }
    } catch (error) {
      console.error('Error adding to keep pile:', error);
      throw error;
    }

    // No API base URL configured. Unlike the read paths there is no mock to
    // fall back to, and returning quietly would report a save that never
    // happened — the pile would look right until the next reload emptied it.
    throw new Error('Keep pile is unavailable: REACT_APP_API_BASE_URL is not configured.');
  }

  async removeFromKeepPile(userId: string, targetUserId: string): Promise<void> {
    try {
      if (this.baseUrl) {
        const response = await fetch(`${MATCHING_BASE}/keep/${targetUserId}`, {
          method: 'DELETE',
          headers: { 'Authorization': this.getAuthHeader() }
        });
        await this.unwrap(response);
        return;
      }
    } catch (error) {
      console.error('Error removing from keep pile:', error);
      throw error;
    }
  }

  // Get the user's full keep pile (persisted server-side), enriched with
  // profile data so it renders fully on reload. Empty on failure.
  // Today's swipe allowance, and how much of it is spent.
  // (see getDailyLimitStatus below)

  // How many cards this account may hold, and how many it is holding.
  // Capacity is a subscription perk, so it comes from the server rather than a
  // client constant — a hardcoded number would either understate what a
  // subscriber paid for or hand a free account slots the backend rejects.
  async getKeepPileAllowance(): Promise<KeepPileAllowance | null> {
    if (!this.baseUrl) return null;
    try {
      const response = await fetch(`${MATCHING_BASE}/keep/allowance`, {
        method: 'GET',
        headers: { 'Authorization': this.getAuthHeader() },
      });
      return await this.unwrap(response);
    } catch (error) {
      // Null, not a default: the caller keeps its conservative starting value
      // rather than being told a capacity that may be wrong in either
      // direction.
      console.error('Error loading keep pile allowance:', error);
      return null;
    }
  }

  async getKeepPileEntries(): Promise<KeepPileEntry[]> {
    try {
      if (this.baseUrl) {
        const response = await fetch(`${MATCHING_BASE}/keep`, {
          method: 'GET',
          headers: { 'Authorization': this.getAuthHeader() },
        });
        const data = await this.unwrap(response);
        return (data ?? []).map(toKeepPileEntry);
      }
    } catch (error) {
      console.error('Error loading keep pile:', error);
    }
    return [];
  }

  // The full set of profile ids queued in the user's deck, in one request.
  // Callers rendering a list use this instead of calling isInDeck() per row —
  // a feed page of 20 would otherwise fire 20 sequential round trips.
  /**
   * The viewer's own swipes, as two sets the feed can seed its buttons from.
   *
   * The heart and X were session-only state, so a reload forgot them. This is
   * the same shape of fix `getDeckProfileIds` already provides for the deck
   * icon — the one button on the card whose state did survive a refresh.
   */
  async getSwipedProfileIds(): Promise<{ liked: Set<string>; passed: Set<string> }> {
    const empty = { liked: new Set<string>(), passed: new Set<string>() };
    if (!this.baseUrl) return empty;

    try {
      const response = await fetch(`${MATCHING_BASE}/swipes?limit=500`, {
        method: 'GET',
        headers: { 'Authorization': this.getAuthHeader() },
      });
      const rows = await this.unwrap(response);

      const liked = new Set<string>();
      const passed = new Set<string>();
      for (const row of Array.isArray(rows) ? rows : []) {
        const id = row.profileId ?? row.profile_id;
        if (!id) continue;
        // One row per person (the table is unique on swiper/swiped), so the
        // action IS the current answer — no ordering to reconcile.
        const action = String(row.action ?? '').toLowerCase();
        if (action === 'like' || action === 'superlike' || action === 'keep') liked.add(id);
        else if (action === 'pass' || action === 'discard') passed.add(id);
      }
      return { liked, passed };
    } catch (error) {
      // Buttons that cannot be restored are a cosmetic loss, not a reason to
      // fail the feed.
      console.error('Could not read previous swipes:', error);
      return empty;
    }
  }

  async getDeckProfileIds(userId: string): Promise<Set<string>> {
    if (this.baseUrl) {
      const response = await fetch(`${MATCHING_BASE}/deck`, {
        method: 'GET',
        headers: { 'Authorization': this.getAuthHeader() }
      });
      const data = await this.unwrap(response);
      return new Set((Array.isArray(data) ? data : []).map((r: any) => r.profile_id ?? r.profileId));
    }

    const deckKey = `swipe_deck_${userId}`;
    const deck = JSON.parse(localStorage.getItem(deckKey) || '[]');
    return new Set(deck.map((d: any) => (typeof d === 'string' ? d : d?.profileId)).filter(Boolean));
  }

  // Count of profiles the user has queued into their deck via "Add to Deck".
  async getDeckCount(userId: string): Promise<number> {
    if (this.baseUrl) {
      const response = await fetch(`${MATCHING_BASE}/deck`, {
        method: 'GET',
        headers: { 'Authorization': this.getAuthHeader() }
      });
      const data = await this.unwrap(response);
      return Array.isArray(data) ? data.length : 0;
    }

    // Mock implementation - read from localStorage
    const deckKey = `swipe_deck_${userId}`;
    const deck = JSON.parse(localStorage.getItem(deckKey) || '[]');
    return deck.length;
  }

  // Check if a profile is currently queued in the user's swipe deck.
  async isInDeck(userId: string, targetUserId: string): Promise<boolean> {
    // Reads go to the server too, so the button can't show "Added" off the
    // back of a stale localStorage entry the database never received.
    if (this.baseUrl) {
      const response = await fetch(`${MATCHING_BASE}/deck/${targetUserId}/check`, {
        method: 'GET',
        headers: { 'Authorization': this.getAuthHeader() }
      });
      const data = await this.unwrap(response);
      return data.isInDeck || false;
    }

    // Mock implementation - check localStorage
    const deckKey = `swipe_deck_${userId}`;
    const deck = JSON.parse(localStorage.getItem(deckKey) || '[]');
    return deck.includes(targetUserId);
  }

  // Bet Requests — the pending-negotiation layer that precedes a real
  // conversation (propose / accept / decline / counter). Unlike most methods
  // here there is no mock fallback; these throw on failure so callers can
  // surface the error to the user.
  async proposeBetRequest(receiverId: string, betLevel: BetLevel, message?: string): Promise<MatchRequest> {
    const response = await fetch(`${MATCHING_BASE}/bet-requests`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': this.getAuthHeader() },
      body: JSON.stringify({ receiverId, betLevel, message }),
    });
    return toMatchRequest(await this.unwrap(response));
  }

  async getBetRequests(direction: 'incoming' | 'outgoing' | 'all' = 'incoming'): Promise<MatchRequest[]> {
    const response = await fetch(`${MATCHING_BASE}/bet-requests?direction=${direction}`, {
      method: 'GET',
      headers: { 'Authorization': this.getAuthHeader() },
    });
    const data = await this.unwrap(response);
    return (data ?? []).map(toMatchRequest);
  }

  async respondToBetRequest(
    requestId: string,
    action: 'accept' | 'decline' | 'counter',
    counterBet?: BetLevel,
    counterMessage?: string
  ): Promise<MatchRequest> {
    const response = await fetch(`${MATCHING_BASE}/bet-requests/${requestId}/respond`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': this.getAuthHeader() },
      body: JSON.stringify({ action, counterBetLevel: counterBet, counterMessage }),
    });
    return toMatchRequest(await this.unwrap(response));
  }

  async cancelBetRequest(requestId: string): Promise<void> {
    const response = await fetch(`${MATCHING_BASE}/bet-requests/${requestId}`, {
      method: 'DELETE',
      headers: { 'Authorization': this.getAuthHeader() },
    });
    await this.unwrap(response);
  }

  // Remove a profile from the user's swipe deck queue.
  async removeFromDeck(userId: string, targetUserId: string): Promise<void> {
    // Throws on failure for the same reason addToDeck does.
    if (this.baseUrl) {
      const response = await fetch(`${MATCHING_BASE}/deck/${targetUserId}`, {
        method: 'DELETE',
        headers: { 'Authorization': this.getAuthHeader() }
      });
      await this.unwrap(response);
      return;
    }

    // Mock implementation - remove from localStorage
    const deckKey = `swipe_deck_${userId}`;
    const existingDeck = JSON.parse(localStorage.getItem(deckKey) || '[]');
    const updatedDeck = existingDeck.filter((id: string) => id !== targetUserId);
    localStorage.setItem(deckKey, JSON.stringify(updatedDeck));
    logger.log(`Removed user ${targetUserId} from deck for user ${userId}`);
  }
}

// Create singleton instance - defaults to the app's real API base URL;
// every method above falls back to mock data if the call fails (e.g. the
// backend hasn't implemented this route yet), so this is safe pre-backend
// and starts returning real data automatically once routes exist.
export const swipeService = new SwipeService();

// Export for testing
export { SwipeService };
